import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo, Wordmark } from "./Logo";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";

export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.resize();
    window.__lenis.scrollTo(el, { offset: -90 });
  }
  else el.scrollIntoView({ behavior: "smooth" });
};

export const LangSwitcher = ({ up = false, align = "right" }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="lang-switcher-btn"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="flex items-center gap-1.5 text-sm font-medium uppercase rounded-full border px-4 py-2 transition-colors duration-300 hover:border-[var(--camel)]"
        style={{ borderColor: "var(--line)", color: "var(--on-surface)", letterSpacing: "0.08em" }}
      >
        {lang}
        <ChevronDown size={14} style={{ color: "var(--camel)" }} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: up ? 8 : -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: up ? 8 : -8 }}
            transition={{ duration: 0.25 }}
            className={`absolute z-50 min-w-[150px] rounded-2xl border py-2 shadow-xl ${align === "left" ? "left-0" : "right-0"} ${up ? "bottom-full mb-2" : "top-full mt-2"}`}
            style={{ background: "var(--surface)", borderColor: "var(--line)" }}
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className="flex w-full items-center justify-between px-4 py-2 text-sm transition-colors duration-200 hover:bg-[var(--surface-secondary)]"
                style={{ color: lang === l.code ? "var(--camel)" : "var(--on-surface)" }}
              >
                {l.label}
                {lang === l.code && <span style={{ color: "var(--gold)" }}>✦</span>}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Navbar() {
  const { t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (l) => {
    setMobileOpen(false);
    if (l.route) {
      if (location.pathname !== l.route) {
        navigate(l.route);
        if (l.id) setTimeout(() => scrollToSection(l.id), 400);
      } else if (l.id) {
        scrollToSection(l.id);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(l.id), 400);
    } else {
      scrollToSection(l.id);
    }
  };

  const links = [
    { id: "how", label: t.nav.how },
    { id: "features", label: t.nav.features },
    { id: "garderobe", label: t.nav.wardrobe },
    { route: "/business", id: null, key: "boutiquen", label: t.nav.boutiques },
    { route: "/business", id: "preise", key: "preise", label: t.nav.pricing },
    { id: "faq", label: t.nav.faq },
  ];

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-md"
      style={{ background: "rgba(252,250,248,0.82)", borderColor: "var(--line)" }}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-3.5">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <Logo size={36} />
          <Wordmark className="text-sm sm:text-base" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.key || l.id}
              data-testid={`nav-link-${l.key || l.id}`}
              onClick={() => go(l)}
              className="gold-underline text-sm font-medium transition-colors duration-300 hover:text-[var(--camel)]"
              style={{ color: "var(--on-surface)" }}
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/mira-plus"
            data-testid="nav-link-plus"
            className="gold-underline text-sm font-semibold transition-colors duration-300 hover:text-[var(--camel)]"
            style={{ color: "var(--camel)" }}
          >
            Mira Plus
          </Link>
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="gold-underline text-sm font-medium transition-colors duration-300 hover:text-[var(--camel)]"
            style={{ color: "var(--on-surface)" }}
          >
            {t.nav.blog}
          </Link>
          <Link
            to="/kontakt"
            data-testid="nav-link-kontakt"
            className="gold-underline text-sm font-medium transition-colors duration-300 hover:text-[var(--camel)]"
            style={{ color: "var(--on-surface)" }}
          >
            {t.nav.contact}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <LangSwitcher />
          <button
            data-testid="nav-download-btn"
            onClick={() => go({ id: "download" })}
            className="pill-btn hidden sm:inline-flex rounded-full px-6 py-2.5 text-sm font-medium text-white"
            style={{ background: "var(--camel)" }}
          >
            {t.nav.download}
          </button>
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden border-t"
            style={{ background: "var(--surface)", borderColor: "var(--line)" }}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-4 gap-1">
              {links.map((l) => (
                <button
                  key={l.key || l.id}
                  data-testid={`mobile-nav-link-${l.key || l.id}`}
                  onClick={() => go(l)}
                  className="py-3 text-left text-base font-medium border-b last:border-0"
                  style={{ borderColor: "var(--line)" }}
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/mira-plus"
                data-testid="mobile-nav-link-plus"
                onClick={() => setMobileOpen(false)}
                className="py-3 text-left text-base font-semibold border-b"
                style={{ borderColor: "var(--line)", color: "var(--camel)" }}
              >
                Mira Plus
              </Link>
              <Link
                to="/blog"
                data-testid="mobile-nav-link-blog"
                onClick={() => setMobileOpen(false)}
                className="py-3 text-left text-base font-medium border-b"
                style={{ borderColor: "var(--line)" }}
              >
                {t.nav.blog}
              </Link>
              <Link
                to="/kontakt"
                data-testid="mobile-nav-link-kontakt"
                onClick={() => setMobileOpen(false)}
                className="py-3 text-left text-base font-medium border-b"
                style={{ borderColor: "var(--line)" }}
              >
                {t.nav.contact}
              </Link>
              <button
                data-testid="mobile-nav-download"
                onClick={() => go({ id: "download" })}
                className="mt-3 rounded-full px-6 py-3 text-sm font-medium text-white text-center"
                style={{ background: "var(--camel)" }}
              >
                {t.nav.download}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
